import React, { useEffect, useRef, useState } from "react";

// AIDEV-NOTE: Breathing animation for meditation loading state
// Guides user through calm breathing while waiting for audio to generate (~10s)
// Simple inhale/exhale pattern with gradual size changes

type BreathPhase = "inhale" | "exhale";

const INHALE_DURATION = 4000; // 4 seconds inhale
const EXHALE_DURATION = 4000; // 4 seconds exhale
const PHASE_LABELS: Record<BreathPhase, string> = {
    inhale: "Breathe in",
    exhale: "Breathe out",
};

// AIDEV-NOTE: Brand forest green shades for color transition
const FOREST_LIGHT = { r: 90, g: 149, b: 120 }; // #5A9578 (brand-forest-500)
const FOREST_DARK = { r: 45, g: 95, b: 74 }; // #2D5F4A (brand-forest-700)

interface BreathingAnimationProps {
    className?: string;
}

export function BreathingAnimation({
    className = "",
}: BreathingAnimationProps) {
    const [phase, setPhase] = useState<BreathPhase>("inhale");
    const [phaseProgress, setPhaseProgress] = useState(0);
    const [opacity, setOpacity] = useState(0); // Start invisible for fade-in
    const animationRef = useRef<number>();
    const phaseStartTimeRef = useRef<number>(Date.now());

    // AIDEV-NOTE: Fade in on mount
    useEffect(() => {
        // Trigger fade-in after a small delay to ensure CSS transition works
        const fadeInTimeout = setTimeout(() => {
            setOpacity(1);
        }, 50);
        return () => clearTimeout(fadeInTimeout);
    }, []);

    // AIDEV-NOTE: Cycle through breath phases with smooth animation
    useEffect(() => {
        const animate = () => {
            const now = Date.now();
            const elapsed = now - phaseStartTimeRef.current;
            const duration =
                phase === "inhale" ? INHALE_DURATION : EXHALE_DURATION;
            const progress = Math.min(elapsed / duration, 1);
            setPhaseProgress(progress);

            if (elapsed >= duration) {
                // AIDEV-NOTE: Reset progress to 0 BEFORE changing phase to prevent flash
                // Without this, there's a render where phase="exhale" but progress=1,
                // causing scale to jump to 0.5 (minimum) momentarily.
                phaseStartTimeRef.current = now;
                setPhaseProgress(0);
                setPhase(phase === "inhale" ? "exhale" : "inhale");
            }

            animationRef.current = requestAnimationFrame(animate);
        };

        animationRef.current = requestAnimationFrame(animate);

        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [phase]);

    // AIDEV-NOTE: Calculate circle scale based on breath phase
    // Uses smooth easing for gradual size changes
    const getScale = (): number => {
        // Smooth ease-in-out curve for gradual transitions
        const easeInOut = (t: number) =>
            t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const easedProgress = easeInOut(phaseProgress);

        // Gradual fill for inhale, gradual shrink for exhale
        if (phase === "inhale") {
            return 0.5 + easedProgress * 0.5; // 0.5 -> 1.0
        } else {
            return 1.0 - easedProgress * 0.5; // 1.0 -> 0.5
        }
    };

    const scale = getScale();

    // AIDEV-NOTE: Interpolate color between light and dark green based on scale
    // Inhale (expanding): light -> dark, Exhale (contracting): dark -> light
    const colorProgress = (scale - 0.5) / 0.5; // 0 at min size, 1 at max size
    const r = Math.round(
        FOREST_LIGHT.r + (FOREST_DARK.r - FOREST_LIGHT.r) * colorProgress,
    );
    const g = Math.round(
        FOREST_LIGHT.g + (FOREST_DARK.g - FOREST_LIGHT.g) * colorProgress,
    );
    const b = Math.round(
        FOREST_LIGHT.b + (FOREST_DARK.b - FOREST_LIGHT.b) * colorProgress,
    );

    return (
        <div
            className={`flex flex-col items-center justify-center gap-8 transition-opacity duration-1000 ${className}`}
            style={{ opacity }}
        >
            {/* AIDEV-NOTE: Breathing circle with glow effect - uses brand forest green */}
            {/* AIDEV-NOTE: Using transform scale for smooth GPU-accelerated animation */}
            <div
                className="relative flex items-center justify-center"
                style={{ width: 240, height: 240 }}
            >
                {/* Outer glow */}
                <div
                    className="absolute rounded-full"
                    style={{
                        width: 240,
                        height: 240,
                        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.15)`,
                        filter: "blur(20px)",
                        transform: `scale(${scale})`,
                    }}
                />
                {/* Inner glow */}
                <div
                    className="absolute rounded-full"
                    style={{
                        width: 220,
                        height: 220,
                        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.25)`,
                        filter: "blur(10px)",
                        transform: `scale(${scale})`,
                    }}
                />
                {/* Main circle */}
                <div
                    className="rounded-full"
                    style={{
                        width: 200,
                        height: 200,
                        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.6)`,
                        boxShadow: `0 0 40px rgba(${r}, ${g}, ${b}, 0.4)`,
                        transform: `scale(${scale})`,
                    }}
                />
            </div>

            {/* AIDEV-NOTE: Breath instruction text */}
            <div className="text-center">
                <p
                    className="text-xl font-medium"
                    style={{ color: `rgb(${r}, ${g}, ${b})` }}
                >
                    {PHASE_LABELS[phase]}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                    Preparing your meditation...
                </p>
            </div>
        </div>
    );
}
